import { useEffect, useMemo, useState } from 'react';
import type { VodPlayable, VodTitle } from '../types';
import { Icon } from './Icon';
import { normalizePlaybackMediaType } from '../player/mediaType';

interface Props {
  kind: 'movie' | 'series';
  titles: VodTitle[];
  detail: VodTitle | null;
  detailLoading: boolean;
  onSelect: (title: VodTitle) => void;
  onCloseDetail: () => void;
  onPlay: (item: VodPlayable) => void;
  favoriteIds: Set<number>;
  onToggleFavorite: (title: VodTitle) => void;
}

function duration(seconds: number | null) {
  if (!seconds) return '';
  const minutes = Math.round(seconds / 60);
  return minutes >= 60 ? `${Math.floor(minutes / 60)}h ${minutes % 60}min` : `${minutes}min`;
}

function normalize(value: string) {
  return value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLocaleLowerCase('pt-BR');
}

export function VodScreen({
  kind, titles, detail, detailLoading, onSelect, onCloseDetail, onPlay,
  favoriteIds, onToggleFavorite,
}: Props) {
  const [preview, setPreview] = useState(titles[0]);
  const [genre, setGenre] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [onlyFavorites, setOnlyFavorites] = useState(false);
  const genres = useMemo(() => Array.from(new Map(
    titles.flatMap(title => title.genres).map(item => [item.id, item]),
  ).values()).slice(0, 8), [titles]);
  const normalizedQuery = normalize(query.trim());
  const visible = titles.filter(title => {
    if (genre && !title.genres.some(item => item.id === genre)) return false;
    if (onlyFavorites && !favoriteIds.has(title.id)) return false;
    if (!normalizedQuery) return true;
    return normalize([
      title.name, title.description, title.year || '',
      ...title.genres.map(item => item.name),
    ].join(' ')).includes(normalizedQuery);
  });
  const activePreview = visible.some(title => title.id === preview?.id) ? preview : visible[0];
  const favoriteCount = titles.filter(title => favoriteIds.has(title.id)).length;

  useEffect(() => {
    if (!searchOpen) return;
    const close = (event: KeyboardEvent) => {
      if (!['Escape', 'Backspace', 'BrowserBack'].includes(event.key) && ![461, 10009].includes(event.keyCode)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      setSearchOpen(false);
    };
    document.addEventListener('keydown', close, true);
    return () => document.removeEventListener('keydown', close, true);
  }, [searchOpen]);

  if (detailLoading) return <main className="content-shell vod-status"><span className="spinner" /><strong>Carregando detalhes…</strong></main>;
  if (detail) {
    const playable = detail.kind === 'movie' && detail.stream_url ? {
      id: `movie-${detail.id}`, name: detail.name, description: detail.description,
      artwork: detail.backdrop || detail.poster, stream_url: detail.stream_url,
      mediaType: normalizePlaybackMediaType(detail.stream_type),
      subtitle: [detail.year, duration(detail.duration_seconds)].filter(Boolean).join(' • '),
      title_id: detail.id, ad_markers: detail.ad_markers || [],
    } : null;
    const favorite = favoriteIds.has(detail.id);
    return (
      <main className="vod-detail" data-testid="vod-detail" style={{ backgroundImage: `linear-gradient(90deg, #0a0a0c 8%, rgba(10,10,12,.93) 46%, rgba(10,10,12,.3)), url("${detail.backdrop}")` }}>
        <button autoFocus={!playable} data-focusable="true" data-autofocus={!playable ? 'true' : undefined} data-focus-zone="detail-actions" className="vod-back" onClick={onCloseDetail}>← Voltar</button>
        <section className="vod-detail__copy">
          <span className="eyebrow">{detail.kind === 'movie' ? 'FILME' : 'SÉRIE'}</span>
          <h1>{detail.name}</h1>
          <div className="vod-meta"><b>{detail.age_rating || 'Livre'}</b><span>{detail.year}</span><span>{duration(detail.duration_seconds)}</span><span>{detail.genres.map(item => item.name).join(' • ')}</span></div>
          <p>{detail.description || 'Sinopse não informada.'}</p>
          <div className="vod-detail__actions">
            {playable && <button autoFocus data-focusable="true" data-autofocus="true" data-focus-zone="detail-actions" className="watch-button" onClick={() => onPlay(playable)} data-testid="vod-watch"><Icon name="play" /> Assistir agora</button>}
            <button data-focusable="true" data-focus-zone="detail-actions" className={`favorite-button ${favorite ? 'active' : ''}`} onClick={() => onToggleFavorite(detail)} data-testid="vod-detail-favorite">{favorite ? '★ Na minha lista' : '☆ Adicionar à lista'}</button>
          </div>
        </section>
        {detail.kind === 'series' && <section className="episode-rail" aria-label="Episódios">
          {(detail.seasons || []).map(season => <div key={season.id} className="season-block"><h2>{season.name}</h2><div className="episode-grid">
            {season.episodes.map(episode => <button key={episode.id} data-focusable="true" data-focus-zone={`season-${season.id}`} data-testid={`episode-${episode.id}`} onClick={() => onPlay({
              id: `episode-${episode.id}`, name: episode.name, description: episode.description,
              artwork: episode.poster || detail.backdrop || detail.poster, stream_url: episode.stream_url,
              mediaType: normalizePlaybackMediaType(episode.stream_type),
              subtitle: `${detail.name} • T${season.number} E${episode.number}`,
              title_id: detail.id, episode_id: episode.id,
              ad_markers: episode.ad_markers || [],
            })}><span className="episode-number">{episode.number}</span><span><strong>{episode.name}</strong><small>{duration(episode.duration_seconds)}</small></span><Icon name="play" /></button>)}
          </div></div>)}
        </section>}
      </main>
    );
  }

  return (
    <main className="content-shell" data-testid="vod-catalog">
      <header className="topbar"><div><span className="eyebrow">CATÁLOGO INNOVA</span><h1>{kind === 'movie' ? 'Filmes' : 'Séries'}</h1><p>Busque, favorite ou pressione Enter para ver os detalhes.</p></div>
        <div className="vod-header-actions" aria-label="Busca e biblioteca">
          <button aria-label={query ? `Editar busca: ${query}` : kind === 'movie' ? 'Buscar filmes' : 'Buscar séries'} data-focusable="true" data-autofocus="true" data-content-autofocus="true" data-focus-zone="vod-tools" data-focus-key="vod-search-trigger" data-testid="vod-search-trigger" className={`vod-search-trigger ${query ? 'active' : ''}`} onClick={() => setSearchOpen(true)}><Icon name="search" /></button>
          <button aria-label={`Minha lista, ${favoriteCount} ${favoriteCount === 1 ? 'item' : 'itens'}`} data-focusable="true" data-focus-zone="vod-tools" data-testid="vod-favorites-filter" className={`vod-favorites-filter ${onlyFavorites ? 'active' : ''}`} onClick={() => setOnlyFavorites(value => !value)}>★ Minha lista</button>
        </div>
      </header>
      {searchOpen && <div className="search-overlay" data-testid="search-overlay" role="dialog" aria-label="Buscar no catálogo">
        <div className="search-overlay__panel"><span className="eyebrow">BUSCA</span><h2>O que você quer assistir?</h2>
          <label className="vod-search"><Icon name="search" /><input autoFocus data-focusable="true" data-autofocus="true" data-focus-zone="search" data-testid="vod-search" value={query} onChange={event => setQuery(event.target.value)} placeholder="Digite o título, gênero ou ano" /></label>
          <button data-focusable="true" data-focus-zone="search" className="secondary-button" onClick={() => setSearchOpen(false)}>Ver {visible.length} resultados</button>
        </div>
      </div>}
      {activePreview && <section className="vod-hero" style={{ backgroundImage: `linear-gradient(90deg, rgba(10,10,12,.98), rgba(10,10,12,.55)), url("${activePreview.backdrop}")` }}>
        <div><span className="eyebrow">EM DESTAQUE</span><h2>{activePreview.name}</h2><p>{activePreview.description || 'Descubra este título no catálogo Innova.'}</p><div className="vod-hero__actions"><button data-focusable="true" data-focus-zone="vod-hero" onClick={() => onSelect(activePreview)}>Ver detalhes</button><button data-focusable="true" data-focus-zone="vod-hero" className={`hero-favorite ${favoriteIds.has(activePreview.id) ? 'active' : ''}`} onClick={() => onToggleFavorite(activePreview)}>{favoriteIds.has(activePreview.id) ? '★ Na lista' : '☆ Minha lista'}</button></div></div>
      </section>}
      <div className="category-row"><button data-focusable="true" data-focus-zone="vod-categories" className={!genre ? 'active' : ''} onClick={() => setGenre(null)}>Todos</button>{genres.map(item => <button key={item.id} data-focusable="true" data-focus-zone="vod-categories" className={genre === item.id ? 'active' : ''} onClick={() => setGenre(item.id)}>{item.name}</button>)}</div>
      {visible.length ? <section className="vod-grid" aria-label={kind === 'movie' ? 'Filmes' : 'Séries'}>{visible.map(title => <article className="vod-card" key={title.id}>
        <button className="vod-card__main" data-focusable="true" data-focus-zone="vod-grid" data-focus-key={`vod-${title.id}`} data-testid={`vod-${title.id}`} aria-label={`${title.name}${title.year ? `, ${title.year}` : ''}`} onFocus={() => setPreview(title)} onClick={() => onSelect(title)}><img src={title.poster} alt="" loading="lazy" /><span className="vod-card__title">{title.name}</span></button>
      </article>)}</section> : <section className="vod-empty" data-testid="vod-empty"><strong>Nenhum título encontrado</strong><p>Ajuste a busca, o gênero ou o filtro Minha lista.</p></section>}
    </main>
  );
}
